import React, { useEffect, useState } from "react";
import { FileBarChart, Download, FileSpreadsheet } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { apiError, arr, downloadFile } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import StatCard from "@/components/common/StatCard";
import ChartCard, { chartTooltipStyle } from "@/components/common/ChartCard";
import EmptyState from "@/components/common/EmptyState";

export default function AdminReports() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [report, setReport] = useState(null);

  useEffect(() => {
    // Fetch up to 100 clients for the dropdown — paginated API
    api.get("/admin/clients", { params: { limit: 100, page: 1 } }).then(({ data }) => {
      const list = arr(data?.data);
      setClients(list);
      if (list[0]) setClientId(list[0].id);
    }).catch(() => setClients([]));
  }, []);

  useEffect(() => {
    if (!clientId) return;
    api.get("/reports/monthly", { params: { clientId } }).then(({ data }) => setReport(data)).catch(() => {});
  }, [clientId]);

  const download = async (type) => {
    try {
      await downloadFile(`/reports/export-${type}`, { clientId }, `report.${type === "pdf" ? "pdf" : "xlsx"}`);
      toast.success(`${type.toUpperCase()} downloaded`);
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Reports" subtitle="Per-client performance reports" icon={FileBarChart}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => download("pdf")} data-testid="export-pdf-btn"><Download className="mr-1 h-4 w-4" /> PDF</Button>
            <Button variant="outline" className="rounded-xl" onClick={() => download("excel")}><FileSpreadsheet className="mr-1 h-4 w-4" /> Excel</Button>
          </div>
        } />

      <div className="w-full sm:w-72">
        <Select value={clientId} onValueChange={setClientId}>
          <SelectTrigger className="h-10 rounded-xl" data-testid="report-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
          <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.businessName}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {!report ? (
        <EmptyState icon={FileBarChart} title="Select a client" description="Choose a client to view their monthly report." />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard label="Rating" value={report.summary.rating} accent="amber" />
            <StatCard label="Total Reviews" value={report.summary.totalReviews} accent="brand" />
            <StatCard label="New Reviews" value={report.summary.newReviews} accent="green" />
            <StatCard label="Replies Sent" value={report.summary.repliesSent} accent="blue" />
            <StatCard label="Posts Published" value={report.summary.postsPublished} accent="purple" />
            <StatCard label="Requests Sent" value={report.summary.requestsSent} accent="brand" />
            <StatCard label="Negative Handled" value={report.summary.negativeHandled} accent="red" />
          </div>
          <ChartCard title="Review Growth" subtitle="Last 6 months">
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={arr(report.reviewGrowth)}>
                <defs><linearGradient id="rr" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#4e67eb" stopOpacity={0.3} /><stop offset="95%" stopColor="#4e67eb" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <Tooltip {...chartTooltipStyle()} />
                <Area type="monotone" dataKey="reviews" stroke="#4e67eb" strokeWidth={2.5} fill="url(#rr)" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
          <div className="card-surface p-6">
            <h3 className="font-heading text-base font-semibold text-slate-800">Recommendations</h3>
            <ul className="mt-3 space-y-2">
              {arr(report.recommendations).map((r, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-600">• {r}</li>)}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
